import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError } from '../api';
import { disputeEndpoints } from '../api-group-sales';
import type { Dispute } from '../api-group-sales';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { dollars } from './event/shared';

/**
 * Admin → Chargebacks (D-100, docs/chargebacks.md).
 *
 * The money side of a dispute has been handled since Phase 10. This page is
 * the side that was missing: whether the tickets still admit anyone, and how
 * long there is to answer the bank.
 *
 * Revoking is deliberate and reversible, so it is a button a person presses
 * and a button that can be pressed back.
 */

const OUTCOME_TONE: Record<string, Tone> = { PENDING: 'warn', WON: 'ok', LOST: 'bad', WITHDRAWN: 'neutral' };

const hoursUntil = (iso: string): number => Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);

export default function DisputesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [openOnly, setOpenOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const disputes = useQuery<{ disputes: Dispute[] }, ApiError>({ queryKey: ['disputes', openOnly], queryFn: () => disputeEndpoints.list(openOnly) });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['disputes'] });
  const onError = (cause: ApiError) => setError(cause.message);

  const revoke = useMutation({ mutationFn: (id: string) => disputeEndpoints.revokeTickets(id), onSuccess: refresh, onError });
  const reinstate = useMutation({ mutationFn: (id: string) => disputeEndpoints.reinstateTickets(id), onSuccess: refresh, onError });
  const markEvidence = useMutation({ mutationFn: (id: string) => disputeEndpoints.note(id, { evidence_submitted: true }), onSuccess: refresh, onError });

  if (!can('disputes.view')) return <NotPermitted permission="disputes.view" />;
  const rows = disputes.data?.disputes ?? [];
  const pending = rows.filter((row) => row.outcome === 'PENDING');
  const atRisk = pending.filter((row) => row.evidence_due_by && !row.evidence_submitted_at && hoursUntil(row.evidence_due_by) < 72);

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {disputes.error ? <ErrorNote>{disputes.error.message}</ErrorNote> : null}

      <Panel title="Chargebacks" description="A cardholder has told their bank they did not make this purchase. The money is already reversed; what is left to decide is whether the tickets still admit anyone, and what to send the bank.">
        <div className="flex flex-wrap gap-8 px-5 py-4">
          <Stat label="Open" value={String(pending.length)} detail={dollars(pending.reduce((sum, row) => sum + row.amount_cents, 0))} />
          <Stat label="Evidence due within 3 days" value={String(atRisk.length)} tone={atRisk.length > 0 ? 'text-bad' : ''} />
          <Stat label="Tickets still live on open disputes" value={String(pending.reduce((sum, row) => sum + (row.tickets_revoked ? 0 : row.live_ticket_count), 0))} />
        </div>
      </Panel>

      <Panel
        title="Disputes"
        actions={
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Open only
          </label>
        }
      >
        <Table headers={['Opened', 'Order', 'Customer', 'Amount', 'Reason', 'Evidence due', 'Tickets', 'Outcome', '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={9}>{disputes.isLoading ? 'Loading…' : openOnly ? 'No open chargebacks.' : 'No chargebacks.'}</EmptyRow>
          ) : (
            rows.map((row) => {
              const hours = row.evidence_due_by ? hoursUntil(row.evidence_due_by) : null;
              return (
                <tr key={row.id} className="border-b border-line last:border-0 align-top">
                  <td className="px-5 py-3 text-sm">{formatWhen(row.stripe_created_at)}</td>
                  <td className="px-5 py-3 text-sm">
                    <Link to={`/orders/${row.order_id}`} className="font-mono text-live hover:underline">
                      {row.order_number}
                    </Link>
                    {row.event_titles ? <div className="text-xs text-ink-500">{row.event_titles}</div> : null}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <div>{row.customer_name ?? '—'}</div>
                    <div className="text-xs text-ink-500">{row.customer_email}</div>
                  </td>
                  <td className="tabular px-5 py-3 text-sm font-medium">{dollars(row.amount_cents)}</td>
                  <td className="px-5 py-3 text-sm">{row.reason ? row.reason.replaceAll('_', ' ') : '—'}</td>
                  <td className="px-5 py-3 text-sm">
                    {row.evidence_submitted_at ? (
                      <span className="text-ink-500">sent</span>
                    ) : row.evidence_due_by ? (
                      <span className={hours !== null && hours < 72 ? 'font-medium text-bad' : ''}>
                        {formatWhen(row.evidence_due_by)}
                        {hours !== null ? <div className="text-xs">{hours < 0 ? 'passed' : `${hours}h left`}</div> : null}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {row.tickets_revoked ? (
                      <Badge tone="bad">revoked</Badge>
                    ) : row.live_ticket_count > 0 ? (
                      <Badge tone="warn">{row.live_ticket_count} still valid</Badge>
                    ) : (
                      <span className="text-ink-400">none</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <Badge tone={OUTCOME_TONE[row.outcome] ?? 'neutral'}>{row.outcome.toLowerCase()}</Badge>
                  </td>
                  <td className="space-x-2 px-5 py-3 text-right text-sm whitespace-nowrap">
                    {can('disputes.manage') ? (
                      <>
                        {row.tickets_revoked ? (
                          <Button
                            disabled={reinstate.isPending}
                            onClick={() => {
                              if (window.confirm('Let these tickets admit again? Do this when the bank has reversed the dispute, or it was raised in error.')) reinstate.mutate(row.id);
                            }}
                          >
                            Reinstate
                          </Button>
                        ) : row.live_ticket_count > 0 ? (
                          <Button
                            variant="danger"
                            disabled={revoke.isPending}
                            onClick={() => {
                              if (window.confirm(`Stop ${row.live_ticket_count} ticket${row.live_ticket_count === 1 ? '' : 's'} on order ${row.order_number} from admitting anyone? The seats are not put back on sale, and this can be undone.`)) revoke.mutate(row.id);
                            }}
                          >
                            Revoke tickets
                          </Button>
                        ) : null}
                        {row.outcome === 'PENDING' && !row.evidence_submitted_at ? (
                          <Button disabled={markEvidence.isPending} onClick={() => markEvidence.mutate(row.id)}>
                            Evidence sent
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })
          )}
        </Table>
        <p className="px-5 py-3 text-xs text-ink-500">
          Evidence is submitted in the Stripe dashboard; marking it here only records that it was done, so the reminder stops. A revoked ticket stops scanning at once and its seat is not resold — cancel the order if the seat should go back on sale.
        </p>
      </Panel>
    </div>
  );
}

function Stat({ label, value, detail, tone = '' }: { label: string; value: string; detail?: string; tone?: string }) {
  return (
    <div>
      <div className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</div>
      <div className={`tabular mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
      {detail ? <div className="text-xs text-ink-500">{detail}</div> : null}
    </div>
  );
}
